import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Upload, FileText, CheckCircle, XCircle, Loader2, RefreshCw } from "lucide-react";
import { format } from "date-fns";

interface KnowledgeDocument {
  id: string;
  filename: string;
  storage_path: string;
  file_size_bytes: number;
  mime_type: string;
  status: string;
  total_chunks: number;
  error_message?: string;
  created_at: string;
}

export default function AdminKnowledge() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const queryClient = useQueryClient();

  // Fetch documents
  const { data: documents, isLoading } = useQuery<KnowledgeDocument[]>({
    queryKey: ['knowledge-documents'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_knowledge_documents' as any)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []) as unknown as KnowledgeDocument[];
    }
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('tags', JSON.stringify([]));

      const { data, error } = await supabase.functions.invoke('knowledge-base-upload', {
        body: formData
      });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Documento enviado! Processando...');
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
      setSelectedFile(null);
    },
    onError: (error: any) => {
      toast.error(`Erro ao enviar: ${error.message}`);
    }
  });

  // Reprocess mutation
  const reprocessMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const { error } = await supabase.functions.invoke('knowledge-base-processor', {
        body: { document_id: documentId }
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Reprocessamento iniciado!');
      queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] });
    },
    onError: (error: any) => {
      toast.error(`Erro ao reprocessar: ${error.message}`);
    }
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'text/plain', 'text/markdown'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de arquivo inválido. Use PDF, TXT ou MD.');
        return;
      }

      // Validate file size (20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Máximo 20MB.');
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      await uploadMutation.mutateAsync(selectedFile);
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-profit text-white"><CheckCircle className="w-3 h-3 mr-1" />Completo</Badge>;
      case 'processing':
        return <Badge className="bg-yellow-500 text-white"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processando</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Falhou</Badge>;
      default:
        return <Badge variant="secondary"><Loader2 className="w-3 h-3 mr-1" />Pendente</Badge>;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">📚 Base de Conhecimento</h1>
        <p className="text-muted-foreground">
          Envie documentos para treinar a IA do bot Telegram
        </p>
      </div>

      {/* Upload Card */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold mb-4">Enviar Documento</h2>
        
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Input
              type="file"
              accept=".pdf,.txt,.md"
              onChange={handleFileSelect}
              disabled={uploading}
            />
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="min-w-[120px]"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </div>

          {selectedFile && (
            <div className="text-sm text-muted-foreground">
              📄 {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Formatos aceitos: PDF, TXT, MD • Tamanho máximo: 20MB
          </p>
        </div>
      </Card>

      {/* Documents List */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Documentos</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => queryClient.invalidateQueries({ queryKey: ['knowledge-documents'] })}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : documents && documents.length > 0 ? (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <FileText className="w-5 h-5 text-primary" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{doc.filename}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <span>{format(new Date(doc.created_at), 'dd/MM/yyyy HH:mm')}</span>
                      <span>•</span>
                      <span>{(doc.file_size_bytes / 1024).toFixed(1)} KB</span>
                      {doc.total_chunks > 0 && (
                        <>
                          <span>•</span>
                          <span>{doc.total_chunks} chunks</span>
                        </>
                      )}
                    </div>
                    {doc.error_message && (
                      <p className="text-xs text-destructive mt-1">{doc.error_message}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  {getStatusBadge(doc.status)}
                  
                  {(doc.status === 'failed' || doc.status === 'completed') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => reprocessMutation.mutate(doc.id)}
                      disabled={reprocessMutation.isPending}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum documento enviado ainda
          </div>
        )}
      </Card>

      {/* Stats */}
      {documents && documents.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{documents.length}</p>
            <p className="text-sm text-muted-foreground">Total Docs</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">
              {documents.filter(d => d.status === 'completed').length}
            </p>
            <p className="text-sm text-muted-foreground">Processados</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">
              {documents.reduce((sum, d) => sum + (d.total_chunks || 0), 0)}
            </p>
            <p className="text-sm text-muted-foreground">Total Chunks</p>
          </Card>
        </div>
      )}
    </div>
  );
}
